// frontend/src/pages/SignUpPage.tsx
// PRC-016 · A1 (alta) y A2 (revisa tu correo, reenviar, usar otro email).
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import axios from 'axios';
import axiosInstance from '../config/axiosInstance';
import { AuthSplitLayout } from '../modules/pricingJourney/components/JourneyLayouts';
import { JourneyField, Notice, StateIcon, StepList } from '../modules/pricingJourney/components/JourneyBlocks';
import { Eyebrow, JourneyButton, JourneyText } from '../modules/pricingJourney/components/JourneyBasics';
import { J } from '../modules/pricingJourney/journeyTokens';
import { SUPPORT_EMAIL } from '../modules/pricingJourney/journeyBilling';
import PasswordField from '../modules/pricingJourney/components/PasswordField';
import {
  EMAIL_RE, focusFirstError, MIN_PASSWORD_LENGTH, OFFLINE_MESSAGE, RESEND_WAIT_SECONDS, resendConfirmation,
} from '../modules/pricingJourney/journeyAuth';

type FieldKey = 'fullName' | 'companyName' | 'email' | 'password' | 'passwordRepeat';
type FieldErrors = Partial<Record<FieldKey | 'terms', React.ReactNode>>;

// Orden de los campos para dar el foco al primer error.
const FIELD_IDS: Array<[FieldKey | 'terms', string]> = [
  ['fullName', 'a1-name'], ['companyName', 'a1-company'], ['email', 'a1-email'],
  ['password', 'a1-pass'], ['passwordRepeat', 'a1-pass2'], ['terms', 'a1-terms'],
];

const BENEFITS = [
  { title: '30 días sin tarjeta', text: 'Al terminar decides si contratas. No se cobra nada de forma automática.' },
  { title: 'Margin Center completo', text: 'Dónde ganas y pierdes margen por cliente, producto y combinación.' },
  { title: 'Hasta 1.000 optimizaciones de precio', text: 'Evalúa precios con tu propio histórico durante la prueba.' },
];

const SignUpPage: React.FC = () => {
  const [form, setForm] = useState({ fullName: '', companyName: '', email: '', password: '', passwordRepeat: '' });
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // A2
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [emailFailed, setEmailFailed] = useState(false);
  const [resendNotice, setResendNotice] = useState<{ tone: 'ok' | 'warn' | 'danger'; text: string } | null>(null);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return;
    const t = window.setTimeout(() => setWait((w) => w - 1), 1000);
    return () => window.clearTimeout(t);
  }, [wait]);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  // PRC-030: cada campo se valida al salir de él y todos antes de enviar.
  const fieldError = (key: FieldKey): string | undefined => {
    const value = form[key];
    switch (key) {
      case 'fullName': return value.trim() ? undefined : 'Escribe tu nombre y apellidos.';
      case 'companyName': return value.trim() ? undefined : 'Escribe el nombre de tu empresa.';
      case 'email':
        if (!value.trim()) return 'Escribe tu email de trabajo.';
        return EMAIL_RE.test(value.trim()) ? undefined : 'Este email no parece correcto.';
      case 'password':
        if (!value) return 'Elige una contraseña.';
        return value.length >= MIN_PASSWORD_LENGTH ? undefined : `La contraseña necesita al menos ${MIN_PASSWORD_LENGTH} caracteres.`;
      case 'passwordRepeat':
        if (!value) return 'Repite la contraseña.';
        return value === form.password ? undefined : 'Las contraseñas no coinciden.';
      default: return undefined;
    }
  };

  // Al salir de un campo con algo escrito se valida; vacío, se espera al envío.
  const blur = (key: FieldKey) => () => {
    if (!form[key]) return;
    setErrors((er) => ({ ...er, [key]: fieldError(key) }));
  };

  const validate = (): FieldErrors => {
    const er: FieldErrors = {};
    (['fullName', 'companyName', 'email', 'password', 'passwordRepeat'] as FieldKey[]).forEach((key) => {
      const message = fieldError(key);
      if (message) er[key] = message;
    });
    if (!terms) er.terms = 'Para crear la cuenta tienes que aceptar los Términos y la Política de privacidad.';
    return er;
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const er = validate();
    setErrors(er);
    if (Object.values(er).some(Boolean)) {
      focusFirstError(FIELD_IDS.map(([key, id]) => [er[key], id]));
      return;
    }
    setLoading(true);
    try {
      const email = form.email.trim();
      const { data } = await axiosInstance.post('/auth/signup', {
        email, password: form.password, fullName: form.fullName.trim(), companyName: form.companyName.trim(),
      });
      setSentTo(email);
      setEmailFailed(data?.email_sent === false);
      setResendNotice(null);
      setWait(data?.email_sent === false ? 0 : RESEND_WAIT_SECONDS);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setErrors({ email: <>Este email ya tiene una cuenta. <a href="/login">Inicia sesión</a> o <a href="/recuperar-contrasena">recupera la contraseña</a>.</> });
      } else if (axios.isAxiosError(err) && err.response?.status === 422) {
        setErrors({ email: 'Revisa el email y la contraseña: alguno no es válido.' });
      } else if (axios.isAxiosError(err) && !err.response) {
        setFormError(OFFLINE_MESSAGE);
      } else {
        setFormError(`No hemos podido crear la cuenta. Inténtalo de nuevo en unos minutos o escríbenos a ${SUPPORT_EMAIL}.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (!sentTo || wait > 0) return;
    const result = await resendConfirmation(sentTo);
    if (result === 'sent') {
      setEmailFailed(false);
      setResendNotice({ tone: 'ok', text: `Enlace reenviado. Podrás pedir otro dentro de ${RESEND_WAIT_SECONDS} segundos.` });
      setWait(RESEND_WAIT_SECONDS);
    } else if (result === 'limited') {
      setResendNotice({ tone: 'warn', text: `Acabamos de enviarte uno. Podrás pedir otro dentro de ${RESEND_WAIT_SECONDS} segundos.` });
      setWait(RESEND_WAIT_SECONDS);
    } else {
      setResendNotice({ tone: 'danger', text: result === 'offline' ? OFFLINE_MESSAGE : `No hemos podido reenviar el enlace. Inténtalo en unos minutos o escríbenos a ${SUPPORT_EMAIL}.` });
    }
  };

  if (sentTo) {
    return (
      <AuthSplitLayout
        eyebrow="Paso 2 de 3"
        tagline="Para proteger la cuenta de tu empresa, confirma tu email."
        brandExtra={
          <StepList steps={[
            { title: 'Crear la cuenta', state: 'done' },
            { title: 'Confirmar el email', description: 'Para proteger la cuenta de tu empresa.', state: 'current' },
            { title: 'Entrar y empezar la prueba', state: 'pending' },
          ]} />
        }
      >
        <StateIcon tone="info"><MarkEmailReadOutlinedIcon /></StateIcon>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <JourneyText variant="title">Revisa tu correo</JourneyText>
          <JourneyText variant="lead">
            Hemos enviado un enlace de confirmación a <b style={{ color: J.text }}>{sentTo}</b>. Ábrelo desde este ordenador o desde el móvil para activar la cuenta.
          </JourneyText>
        </Box>
        {emailFailed && (
          <Notice tone="danger">
            No hemos podido enviar el email ahora mismo. Tu cuenta está creada: pulsa Reenviar enlace en unos minutos o escríbenos a {SUPPORT_EMAIL}.
          </Notice>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <JourneyText variant="h3">¿No te ha llegado?</JourneyText>
          <Box component="ul" sx={{ m: 0, pl: '18px', fontSize: 14, lineHeight: 1.6, color: J.body }}>
            <li>Puede tardar un par de minutos.</li>
            <li>Mira en la carpeta de spam o de promociones.</li>
            <li>Comprueba que el email está bien escrito.</li>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          <JourneyButton onClick={resend} disabled={wait > 0}>{wait > 0 ? `Reenviar enlace (${wait} s)` : 'Reenviar enlace'}</JourneyButton>
          <JourneyButton kind="ghost" onClick={() => { setSentTo(null); setEmailFailed(false); setResendNotice(null); setForm((f) => ({ ...f, email: '' })); }}>Usar otro email</JourneyButton>
        </Box>
        {resendNotice && <Notice tone={resendNotice.tone}>{resendNotice.text}</Notice>}
        <JourneyText variant="small" sx={{ textAlign: 'center', '& a': { color: J.skyText, fontWeight: 600, textDecoration: 'none' } }}>
          ¿Ya lo has confirmado? <a href="/login">Inicia sesión</a>
        </JourneyText>
      </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout benefits={BENEFITS}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Eyebrow tone="sky">Crear cuenta</Eyebrow>
        <JourneyText variant="title">Crea la cuenta de tu empresa</JourneyText>
        <JourneyText variant="lead">Empieza tu prueba de 30 días. Tarda menos de un minuto.</JourneyText>
      </Box>
      <Box component="form" noValidate onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))' }, gap: '16px' }}>
          <JourneyField id="a1-name" label="Nombre y apellidos" value={form.fullName} onChange={set('fullName')} onBlur={blur('fullName')} error={errors.fullName} autoComplete="name" autoFocus />
          <JourneyField id="a1-company" label="Empresa" value={form.companyName} onChange={set('companyName')} onBlur={blur('companyName')} error={errors.companyName} autoComplete="organization" />
        </Box>
        <JourneyField id="a1-email" label="Email de trabajo" type="email" value={form.email} onChange={set('email')} onBlur={blur('email')} error={errors.email} autoComplete="email" />
        <PasswordField
          id="a1-pass" label="Contraseña" value={form.password} onChange={set('password')} onBlur={blur('password')} autoComplete="new-password"
          error={errors.password} showRequirements
        />
        <PasswordField
          id="a1-pass2" label="Repite la contraseña" value={form.passwordRepeat} onChange={set('passwordRepeat')} onBlur={blur('passwordRepeat')} autoComplete="new-password"
          error={errors.passwordRepeat}
        />
        <Box>
          <Box component="label" sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: 13, color: J.body, lineHeight: 1.5, '& a': { color: J.skyText } }}>
            <Box component="input" id="a1-terms" type="checkbox" checked={terms} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setTerms(e.target.checked); setErrors((er) => ({ ...er, terms: undefined })); }} sx={{ width: 18, height: 18, m: '2px 0 0', accentColor: J.sky }} />
            <span>Acepto los <a href="/terms" target="_blank" rel="noreferrer">Términos del servicio</a> y la <a href="/privacy" target="_blank" rel="noreferrer">Política de privacidad</a>.</span>
          </Box>
          {errors.terms && <Box sx={{ fontSize: 12, color: '#fca5a5', mt: '6px' }}>{errors.terms}</Box>}
        </Box>
        {formError && <Notice tone="danger">{formError}</Notice>}
        <JourneyButton type="submit" block disabled={loading}>{loading ? 'Creando la cuenta…' : 'Crear cuenta y empezar la prueba'}</JourneyButton>
      </Box>
      <JourneyText variant="small" sx={{ textAlign: 'center', '& a': { color: J.skyText, fontWeight: 600, textDecoration: 'none' } }}>
        ¿Ya tienes cuenta? <a href="/login">Inicia sesión</a>
      </JourneyText>
    </AuthSplitLayout>
  );
};

export default SignUpPage;
